import React, { useState, useEffect } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

const G = "#009444";
const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PK", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

function Badge({ text, color = G }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: color + "22",
        color,
        border: `1px solid ${color}44`,
        borderRadius: 20,
        padding: "2px 10px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {text}
    </span>
  );
}

function DetailDrawer({ record, onClose }) {
  if (!record) return null;
  const rows = [
    ["Full Name",    record.name],
    ["CNIC",         record.cnic       || "—"],
    ["Phone",        record.phone      || "—"],
    ["WhatsApp",     record.whatsappNumber || "—"],
    ["Province",     record.province   || "—"],
    ["City",         record.city       || "—"],
    ["Area",         record.area       || "—"],
    ["Address",      record.address    || "—"],
    ["Why Join",     record.whyJoin    || "—"],
    ["Occupation",   record.occupation || "—"],
    ["Contribution", record.contribution || "—"],
    ["Submitted",    fmt(record.createdAt)],
  ];
  return (
    <>
      <div onClick={onClose} style={s.drawerBackdrop} />
      <div style={s.drawer}>
        <div style={s.drawerHeader}>
          <div>
            <div style={s.drawerName}>{record.name}</div>
            <div style={s.drawerSub}>{record.province} · {record.city}</div>
          </div>
          <button onClick={onClose} style={s.drawerClose}>✕</button>
        </div>
        <div style={s.drawerBody}>
          {rows.map(([label, val]) => (
            <div key={label} style={s.drawerRow}>
              <span style={s.drawerLabel}>{label}</span>
              <span style={s.drawerVal}>{val}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function Admin() {
  const [token, setToken]       = useState(() => localStorage.getItem("adminToken"));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading]   = useState(false);
  const [data, setData]         = useState(null);
  const [fetchError, setFetchError] = useState("");
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (token) fetchSubmissions(token); }, [token]);

  const fetchSubmissions = async (t) => {
    try {
      const res = await axios.get(`${BASE_URL}/admin/submissions`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setData(res.data);
    } catch {
      setFetchError("Failed to load data. Please log in again.");
      handleLogout();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/admin/login`, { username, password });
      localStorage.setItem("adminToken", res.data.token);
      setToken(res.data.token);
    } catch {
      setLoginError("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken(null);
    setData(null);
  };

  // ── Login ──────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div style={s.loginPage}>
        <div style={s.loginCard}>
          <div style={s.loginLogo}>T</div>
          <h2 style={s.loginTitle}>Admin Portal</h2>
          <p style={s.loginSubtitle}>Technology Movement Pakistan</p>
          <form onSubmit={handleLogin}>
            <div style={s.field}>
              <label style={s.label}>Username</label>
              <input style={s.input} type="text" value={username}
                onChange={e => setUsername(e.target.value)} required autoComplete="username" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password</label>
              <input style={s.input} type="password" value={password}
                onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            {loginError && <p style={s.error}>{loginError}</p>}
            <button style={s.loginBtn} type="submit" disabled={loading}>
              {loading ? "Logging in…" : "Login →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────────────────────
  const records = data?.records || [];
  const filtered = records.filter(r =>
    !search ||
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.cnic?.includes(search) ||
    r.phone?.includes(search) ||
    r.city?.toLowerCase().includes(search.toLowerCase()) ||
    r.province?.toLowerCase().includes(search.toLowerCase())
  );

  // Province breakdown
  const provCount = records.reduce((acc, r) => {
    if (r.province) acc[r.province] = (acc[r.province] || 0) + 1;
    return acc;
  }, {});
  const topProv = Object.entries(provCount).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div style={s.dashboard}>
      {/* Top bar */}
      <div style={s.topBar}>
        <div style={s.topBarBrand}>
          <span style={s.topBarLogo}>T</span>
          <div>
            <div style={s.topBarTitle}>TMP Admin Dashboard</div>
            <div style={s.topBarSub}>Member Applications</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button style={s.refreshBtn} onClick={() => fetchSubmissions(token)}>↻ Refresh</button>
          <button style={s.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {fetchError && <p style={s.error}>{fetchError}</p>}

      {data ? (
        <div style={s.content}>

          {/* Stat cards */}
          <div style={s.statRow}>
            <div style={s.statCard}>
              <span style={s.statNumber}>{data.count}</span>
              <span style={s.statLabel}>Total Applications</span>
            </div>
            {topProv.map(([prov, cnt]) => (
              <div key={prov} style={{ ...s.statCard, ...s.statCardSm }}>
                <span style={{ ...s.statNumber, fontSize: 32 }}>{cnt}</span>
                <span style={s.statLabel}>{prov}</span>
              </div>
            ))}
          </div>

          {/* Search */}
          <div style={s.searchWrap}>
            <span style={s.searchIcon}>🔍</span>
            <input
              style={s.searchInput}
              placeholder="Search by name, CNIC, phone, city, province…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button style={s.searchClear} onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          {/* Table */}
          <div style={s.tableWrapper}>
            <table style={s.table}>
              <thead>
                <tr style={s.theadRow}>
                  {["#","Name","CNIC","Phone","WhatsApp","Province","City","Submitted",""].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r._id}
                    style={{ ...(i % 2 === 0 ? s.trEven : s.trOdd), cursor: "pointer" }}
                    onClick={() => setSelected(r)}
                  >
                    <td style={{ ...s.td, color: "#555", fontSize: 12 }}>{i + 1}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: "#fff" }}>{r.name}</td>
                    <td style={{ ...s.td, fontFamily: "monospace", color: "#aaa" }}>{r.cnic || "—"}</td>
                    <td style={s.td}>{r.phone || "—"}</td>
                    <td style={s.td}>{r.whatsappNumber || "—"}</td>
                    <td style={s.td}>
                      {r.province ? <Badge text={r.province} /> : "—"}
                    </td>
                    <td style={s.td}>{r.city || "—"}</td>
                    <td style={{ ...s.td, fontSize: 12, color: "#666" }}>
                      {fmt(r.createdAt)}
                    </td>
                    <td style={s.td}>
                      <button style={s.viewBtn} onClick={e => { e.stopPropagation(); setSelected(r); }}>
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p style={s.empty}>
                {search ? `No results for "${search}"` : "No applications yet."}
              </p>
            )}
          </div>
          <p style={s.countNote}>Showing {filtered.length} of {data.count} applications</p>
        </div>
      ) : (
        <p style={s.loading}>Loading…</p>
      )}

      <DetailDrawer record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

const s = {
  /* Login */
  loginPage: { minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#050e08" },
  loginCard: { background:"#0d1f12", border:`1px solid ${G}33`, borderRadius:16, padding:"44px 40px", width:"100%", maxWidth:380, boxShadow:`0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px ${G}22` },
  loginLogo: { width:52, height:52, borderRadius:14, background:G, display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, fontStyle:"italic", color:"#fff", fontWeight:400, marginBottom:20 },
  loginTitle: { color:"#fff", margin:"0 0 4px", fontSize:24, fontWeight:700 },
  loginSubtitle: { color:"#4a7a5a", margin:"0 0 28px", fontSize:13 },
  field: { marginBottom:18 },
  label: { display:"block", color:"#6a9a7a", fontSize:12, fontWeight:600, letterSpacing:0.5, textTransform:"uppercase", marginBottom:6 },
  input: { width:"100%", padding:"11px 14px", background:"rgba(255,255,255,0.05)", border:`1.5px solid rgba(255,255,255,0.1)`, borderRadius:10, color:"#fff", fontSize:14, boxSizing:"border-box", outline:"none" },
  loginBtn: { width:"100%", padding:"13px", background:G, color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", marginTop:8, letterSpacing:0.3 },
  error: { color:"#e55", fontSize:13, margin:"8px 0" },

  /* Dashboard */
  dashboard: { minHeight:"100vh", background:"#070f09", fontFamily:"system-ui,sans-serif" },
  topBar: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 32px", background:"#0d1f12", borderBottom:`1px solid ${G}33` },
  topBarBrand: { display:"flex", alignItems:"center", gap:14 },
  topBarLogo: { width:40, height:40, borderRadius:11, background:G, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontStyle:"italic", color:"#fff", fontWeight:400 },
  topBarTitle: { color:"#fff", fontWeight:700, fontSize:17, lineHeight:1.2 },
  topBarSub: { color:"#4a7a5a", fontSize:12 },
  refreshBtn: { background:"transparent", border:`1px solid ${G}55`, color:G, padding:"7px 16px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600 },
  logoutBtn: { background:"transparent", border:"1px solid #333", color:"#777", padding:"7px 16px", borderRadius:8, cursor:"pointer", fontSize:13 },

  /* Stats */
  content: { padding:"28px 32px" },
  statRow: { display:"flex", gap:16, flexWrap:"wrap", marginBottom:28 },
  statCard: { background:"#0d1f12", border:`1px solid ${G}33`, borderRadius:14, padding:"22px 28px", display:"flex", flexDirection:"column", minWidth:140 },
  statCardSm: { padding:"18px 24px" },
  statNumber: { color:G, fontSize:44, fontWeight:800, lineHeight:1 },
  statLabel: { color:"#4a7a5a", fontSize:13, marginTop:6 },

  /* Search */
  searchWrap: { display:"flex", alignItems:"center", gap:10, background:"#0d1f12", border:`1.5px solid ${G}33`, borderRadius:12, padding:"10px 16px", marginBottom:20 },
  searchIcon: { fontSize:16, flexShrink:0 },
  searchInput: { flex:1, background:"transparent", border:"none", outline:"none", color:"#fff", fontSize:14 },
  searchClear: { background:"transparent", border:"none", color:"#555", cursor:"pointer", fontSize:16, padding:0 },

  /* Table */
  tableWrapper: { overflowX:"auto", background:"#0d1f12", border:`1px solid ${G}22`, borderRadius:14 },
  table: { width:"100%", borderCollapse:"collapse", minWidth:900 },
  theadRow: { background:"#0a1a0f" },
  th: { color:"#3a6a4a", fontSize:11, textTransform:"uppercase", letterSpacing:0.8, padding:"12px 14px", textAlign:"left", borderBottom:`1px solid ${G}22`, fontWeight:700 },
  td: { color:"#ccc", fontSize:13.5, padding:"12px 14px", borderBottom:"1px solid #0f1f14" },
  trEven: { background:"#0d1f12" },
  trOdd: { background:"#0a1a0f" },
  viewBtn: { background:`${G}22`, border:`1px solid ${G}44`, color:G, padding:"5px 12px", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700 },
  countNote: { color:"#3a5a44", fontSize:12, marginTop:12, textAlign:"right" },
  empty: { color:"#3a5a44", textAlign:"center", padding:"48px 32px", fontSize:14 },
  loading: { color:"#3a5a44", textAlign:"center", marginTop:80, fontSize:14 },

  /* Drawer */
  drawerBackdrop: { position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, backdropFilter:"blur(4px)" },
  drawer: { position:"fixed", top:0, right:0, bottom:0, width:"min(420px,94vw)", background:"#0d1f12", borderLeft:`1px solid ${G}33`, zIndex:201, display:"flex", flexDirection:"column", boxShadow:"-16px 0 48px rgba(0,0,0,0.4)" },
  drawerHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"24px 24px 18px", background:`linear-gradient(135deg, ${G}22 0%, transparent 100%)`, borderBottom:`1px solid ${G}22` },
  drawerName: { fontSize:19, fontWeight:700, color:"#fff", marginBottom:4 },
  drawerSub: { fontSize:13, color:"#4a7a5a" },
  drawerClose: { background:`rgba(255,255,255,0.07)`, border:`1px solid rgba(255,255,255,0.12)`, color:"#aaa", width:34, height:34, borderRadius:"50%", cursor:"pointer", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  drawerBody: { flex:1, overflowY:"auto", padding:"20px 24px" },
  drawerRow: { display:"flex", flexDirection:"column", gap:4, padding:"14px 0", borderBottom:"1px solid #0f1f14" },
  drawerLabel: { fontSize:10.5, fontWeight:700, letterSpacing:0.8, textTransform:"uppercase", color:"#3a6a4a" },
  drawerVal: { fontSize:14, color:"#d0e8d8", lineHeight:1.6 },
};
